import { Dialog, InputV2, TextArea } from '@cimplico/components';
import { useUpdateWorksheets } from '../../../queries/use-update-worksheets';
import { Worksheet } from '../../../types/worksheet';
import { Controller, useForm } from 'react-hook-form';

type EditDetailsFormProps = {
    worksheet: Worksheet;
    onClose: () => void;
};

type EditWorksheetFields = {
    name: string;
    description: string | null;
    tag: string | null;
};

function EditDetailsForm({ worksheet, onClose }: EditDetailsFormProps): JSX.Element {
    const {
        control,
        setError,
        formState: { isDirty, dirtyFields },
        handleSubmit,
        getValues,
    } = useForm<EditWorksheetFields>({
        defaultValues: { name: worksheet.name, description: worksheet.description, tag: worksheet.tag },
    });

    const updateWorksheetsQuery = useUpdateWorksheets();

    function handleUpdate(): void {
        const { name, description, tag } = getValues();

        if (!isDirty) {
            onClose();
            return;
        }

        const updatedName = name.trim();
        const updatedDescription = typeof description === 'string' ? description.trim() : description;
        const updatedTag = typeof tag === 'string' ? tag.trim() : description;

        updateWorksheetsQuery.mutate(
            [
                {
                    id: worksheet.id,
                    fields: {
                        name: dirtyFields.name ? updatedName : undefined,
                        description: dirtyFields.description ? (updatedDescription === '' ? null : updatedDescription) : undefined,
                        tag: dirtyFields.tag ? (updatedTag === '' ? null : updatedTag) : undefined,
                    },
                },
            ],
            {
                onSuccess: () => {
                    onClose();
                },
                onError: (e) => {
                    const errorMessage = e.response?.data.results[0].error.fields.errorMessage;
                    setError('name', { message: errorMessage });
                },
            }
        );
    }

    return (
        <Dialog
            type="info"
            title="Edit worksheet"
            description="Do you want to edit the details for this worksheet? Worksheet names must be unique."
            isOpen={true}
            primaryAction="Save"
            secondaryAction="Cancel"
            onClose={onClose}
            onSecondaryActionClick={onClose}
            onPrimaryActionClick={handleSubmit(handleUpdate)}
            isLoading={updateWorksheetsQuery.isLoading}
        >
            <form className="flex flex-col gap-4 pr-6 pb-4" onSubmit={handleSubmit(handleUpdate)}>
                <Controller
                    name="name"
                    rules={{ required: 'A worksheet name must be provided.' }}
                    control={control}
                    render={({ fieldState, field: { value, onChange } }): JSX.Element => (
                        <InputV2
                            type="text"
                            title="Name"
                            onChange={onChange}
                            errorMessage={fieldState.error?.message}
                            defaultValue={value}
                            placeholder="e.g. Tax Reconciliation"
                            maxLength={50}
                            autoFocus
                        />
                    )}
                />
                <Controller
                    name="tag"
                    control={control}
                    render={({ field: { value, onChange } }): JSX.Element => (
                        <InputV2
                            type="text"
                            title="Tag"
                            onChange={onChange}
                            defaultValue={value || undefined}
                            placeholder="Enter a tag..."
                            isOptional
                        />
                    )}
                />
                <Controller
                    name="description"
                    control={control}
                    render={({ field: { value, onChange } }): JSX.Element => (
                        <TextArea
                            title="Description"
                            onChange={onChange}
                            defaultValue={value || undefined}
                            placeholder="Description..."
                            isOptional
                        />
                    )}
                />
                <button type="submit" hidden />
            </form>
        </Dialog>
    );
}

export default EditDetailsForm;
